<script setup>
/**
 * SelectMenu — a labelled single-choice control, styled as FilterSelect's sibling.
 *
 * The filter row is a line of chips with panels; a native <select> dropped among them
 * renders as the operating system's own widget, in the OS font, with none of the border,
 * radius or hover of the things either side of it. That is why "Group by" looked like it
 * came from a different application.
 *
 * FilterSelect is the multi-select of this pair and cannot be reused, because its whole
 * contract — an array model, checkboxes, "clear N selected" — is about choosing several.
 *
 *   <SelectMenu v-model="groupBy" label="Group by" :options="GROUPS" />
 */
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { ChevronDown, Check, Search } from 'lucide-vue-next'
import BaseAvatar from '../BaseAvatar.vue'
import { CONTROL_CHIP, CONTROL_CHIP_IDLE, FIELD_BASE } from '../../constants/controlStandards'
import { typeIconComponent } from '../../constants/ticketTypeIcons'
import { placeMenu } from '../../utils/popoverPlacement'

const props = defineProps({
    /** The panel's layer: raise it when the menu opens inside a dialog above the page. */
    layer: { type: String, default: 'z-[60]' },
    modelValue: { type: [String, Number], default: '' },
    /** Shown to the left of the value, so the chip reads "Group by · Status". */
    label: { type: String, default: '' },
    /** Plain strings, or `{ value, label }` objects — the same shapes FilterSelect takes. */
    options: { type: Array, default: () => [] },
    icon: { type: [Object, Function], default: null },
    /**
     * Tall enough to show every option: lifts the 320px cap on the panel, so a short fixed
     * list like Group by opens with all of it in view instead of a scrollbar hiding the
     * last one or two. The panel still stops at the edge of the screen. (Kenneth, 25 Sep:
     * "increase the height so that everything is visible right away".)
     */
    showAll: { type: Boolean, default: false },

    /**
     * `chip` sits in a filter row beside FilterSelect. `field` is the same control as a
     * form input: full width, 34px tall, the label above it rather than inside.
     *
     * A native <select> cannot be the second one. Its closed box takes CSS, but the list
     * it opens is drawn by the operating system — OS font, OS colours, none of the border
     * or radius of the modal around it — which is why every picker in a Cortex dialog
     * looked like it came from a different application.
     */
    variant: { type: String, default: 'chip' },

    /** Shown when nothing is chosen. The empty first <option> of the control this replaces. */
    placeholder: { type: String, default: 'Select…' },

    /**
     * The `field` variant's height. 34px is the app's form-field standard and what every
     * caller gets by default; pass another only to match a taller neighbour in the same
     * row, such as DatePicker's block mode at h-10.
     */
    fieldHeight: { type: String, default: 'h-[34px]' },

    disabled: { type: Boolean, default: false },

    /** Let the chip shrink inside a tight row, cutting the value with an ellipsis instead of pushing its neighbours onto a new line. Below 440px the label is dropped to keep the value readable (it stays in the accessible name). */
    shrink: { type: Boolean, default: false },

    /**
     * Always offer the search box, however few the options. Parent does, as the ticket page's
     * own parent picker does (Kenneth, 28 Sep): below the usual eight it had none, so the bulk
     * drawer's Parent list could only be scrolled.
     */
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search…' },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const root = ref(null)
const trigger = ref(null)
const panelEl = ref(null)

/**
 * The panel is teleported to <body> and positioned from the trigger's own rectangle.
 *
 * As a child of the component it was clipped the moment an ancestor had `overflow: hidden`
 * — a modal body, a card, a scrolling drawer — and it ran off the bottom of a viewport
 * when the control sat near it. A native <select> never had either problem because the OS
 * draws its list in a layer above the page; this is the same idea, in the page.
 *
 * Flips above the trigger when there is more room there, anchored by its bottom edge so it
 * sits on the trigger whatever its height (utils/popoverPlacement placeMenu), and never leaves
 * the viewport horizontally.
 */
const panel = ref({ top: 0, bottom: null, left: 0, minWidth: 0, maxWidth: 0, maxHeight: 320 })

const MARGIN = 8
const GAP = 4
/** The panel is never narrower than this, and never wider — past it, options truncate. */
const MIN_WIDTH = 180
const MAX_WIDTH = 420

function place() {
    const el = trigger.value
    if (! el) return

    const r = el.getBoundingClientRect()

    // The panel is at LEAST as wide as its trigger and then grows to fit its widest
    // option, rather than being pinned to the trigger's width.
    //
    // It used to be exactly `max(triggerWidth, 180)`, which is fine for "Group by ·
    // Status" and wrong for a list of people: the "Invite a member…" chip is narrow, so
    // every name came out as "Aisha Bello - QA (qua…". A picker whose options cannot be
    // read is not a picker.
    //
    // MAX_WIDTH is a cap, not a target — past it the option truncates, and `title` on each
    // row means the full value is still reachable. Some name-plus-email is always going to
    // be longer than any menu worth showing.
    const minWidth = Math.max(r.width, MIN_WIDTH)
    const maxWidth = Math.min(MAX_WIDTH, window.innerWidth - 2 * MARGIN)

    const at = placeMenu(r, window.innerHeight, props.showAll ? Infinity : 320, MARGIN, GAP)
    panel.value = {
        top: at.above ? null : at.top,
        bottom: at.above ? at.bottom : null,
        left: Math.max(MARGIN, r.left),
        minWidth,
        maxWidth,
        maxHeight: at.maxHeight,
    }
}

/**
 * Pull the panel back on screen once its real width is known.
 *
 * The width is decided by the content, so it cannot be clamped before the panel renders —
 * measuring it afterwards is the only honest way. Clamping against MAX_WIDTH up front
 * would shove every short menu left for a width it was never going to reach.
 */
function clampToViewport() {
    const el = panelEl.value
    if (! el) return

    const overflow = el.getBoundingClientRect().right - (window.innerWidth - MARGIN)
    if (overflow > 0) {
        panel.value = { ...panel.value, left: Math.max(MARGIN, panel.value.left - overflow) }
    }
}

async function toggle() {
    if (props.disabled) return
    open.value = ! open.value
    if (open.value) {
        await nextTick()
        place()
        await nextTick()
        clampToViewport()
    }
}

/**
 * `icon`, `iconColor` and `sublabel` are optional and pass through untouched.
 *
 * A ticket reference means nothing without its title, and a title means nothing without
 * its reference — so a picker for tickets shows the reference as the label and the title
 * beneath it, drawn with the type's own icon, exactly as the ticket page's parent picker
 * has always done.
 */
const normalised = computed(() => props.options.map(o => (
    o !== null && typeof o === 'object'
        ? {
            value: o.value,
            label: String(o.label ?? o.value),
            sublabel: o.sublabel ?? null,
            trigger: o.trigger ?? null,
            // A person chip ({id, name, initials, color, avatar_url}) draws their avatar, as
            // FilterSelect's does: a picker of people is a list of faces, not of names.
            person: o.person ?? null,
            // A STRING icon is a ticket-type key from the API ('bug', 'story'), resolved
            // here the way FilterSelect already resolves it. `<component :is>` given a
            // string looks for a globally registered component, finds none, and renders
            // nothing — so a caller passing `icon: t.icon` straight from the payload showed
            // no icon at all, and nothing said so. An already-resolved component passes
            // through untouched.
            icon: typeof o.icon === 'string' ? typeIconComponent(o.icon) : (o.icon ?? null),
            // `color` too, not only `iconColor`. Two callers have now passed a priority
            // straight from the payload — where the field IS called `color` — and got a
            // grey chevron: the icon resolved, the colour was dropped, and a grey icon
            // looks like a design choice rather than a mistake. Accepting both is cheaper
            // than a third caller finding out the same way.
            iconColor: o.iconColor ?? o.color ?? null,
        }
        : { value: o, label: String(o), sublabel: null, person: null, icon: null, iconColor: null }
)))

/** Above this many, the panel grows a search box — the same rule FilterSelect uses. */
const SEARCH_THRESHOLD = 8
const query = ref('')
const showSearch = computed(() => props.searchable || normalised.value.length > SEARCH_THRESHOLD)

const visibleOptions = computed(() => {
    const q = query.value.trim().toLowerCase()
    if (!q) return normalised.value

    return normalised.value.filter(o =>
        o.label.toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q))
})

const current = computed(() => normalised.value.find(o => o.value === props.modelValue) ?? null)
// An option may name itself differently once chosen (`trigger`): a card shows its key in the list
// and "CARD-58 · its title" on the button, so the choice reads whole.
const currentLabel = computed(() => current.value?.trigger ?? current.value?.label ?? String(props.modelValue ?? ''))
const isEmpty = computed(() => ! current.value && (props.modelValue === null || props.modelValue === undefined || props.modelValue === ''))

function pick(option) {
    emit('update:modelValue', option.value)
    open.value = false
}

function onDocMouseDown(e) {
    if (! open.value) return
    // The panel is no longer inside `root`, so it has to be asked separately or every
    // click on an option would close the menu before the click landed.
    if (root.value?.contains(e.target) || panelEl.value?.contains(e.target)) return
    open.value = false
}
// Escape closes the menu and marks the key used, so a drawer or modal around it that reads
// `defaultPrevented` stays open: Escape on the drawer's Priority closed the whole drawer.
// Listened for in the capture phase, so this runs before any container's own listener,
// whichever mounted first.
function onKeydown(e) {
    if (e.key === 'Escape' && open.value) {
        open.value = false
        e.preventDefault()
    }
}
// A fixed panel does not travel with the page, so anything that moves the trigger has to
// move it too. Capture phase, because the scroller is usually an inner element.
// Options can arrive after the panel opens (a list loaded on hover, a search narrowing it), and the
// panel grows to fit them. Measured only once, a late wide list ran off the screen's edge
// (Kenneth, 8 Oct: Add to card on an email). So it is pulled back whenever its size changes.
let panelResize = null
watch(panelEl, (el) => {
    panelResize?.disconnect()
    panelResize = null
    if (el && typeof ResizeObserver !== 'undefined') {
        panelResize = new ResizeObserver(() => clampToViewport())
        panelResize.observe(el)
    }
})
onBeforeUnmount(() => panelResize?.disconnect())

async function reposition() {
    if (! open.value) return
    place()
    await nextTick()
    clampToViewport()
}

onMounted(() => {
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeydown, true)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
})
onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocMouseDown)
    document.removeEventListener('keydown', onKeydown, true)
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
})

const ITEM = 'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] cursor-pointer whitespace-nowrap text-left'
</script>

<template>
    <div ref="root" :class="variant === 'field' ? 'relative w-full' : ['relative', shrink ? 'min-w-0' : '']">
        <button
            ref="trigger"
            type="button"
            :disabled="disabled"
            :class="variant === 'field'
                ? [FIELD_BASE, fieldHeight, 'flex items-center gap-2 text-left disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed', open ? 'border-blue-500' : '']
                : [CONTROL_CHIP, CONTROL_CHIP_IDLE, shrink ? 'max-w-full min-w-0' : '']"
            :aria-expanded="open"
            :aria-label="shrink && label ? `${label}: ${isEmpty ? placeholder : currentLabel}` : null"
            aria-haspopup="listbox"
            @click="toggle"
        >
            <component :is="icon" v-if="icon" class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <BaseAvatar v-if="current?.person" :person="current.person" size="sm" class="flex-shrink-0" />
            <span v-if="label && variant !== 'field'" class="font-medium text-slate-500" :class="shrink ? 'hidden min-[440px]:inline flex-shrink-0' : ''">{{ label }}</span>
            <!-- The placeholder is lighter, the way an unfilled input's is. A chip has no
                 empty state — it always carries a value — so this only applies to a field. -->
            <span :class="variant === 'field' ? ['flex-1 truncate', isEmpty ? 'text-slate-400' : ''] : (shrink ? 'min-w-0 truncate' : '')">{{ isEmpty ? placeholder : currentLabel }}</span>
            <ChevronDown class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>

        <Teleport to="body">
        <div
            v-if="open"
            ref="panelEl"
            :class="layer" class="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-1.5 overflow-hidden flex flex-col"
            :style="{ ...(panel.bottom !== null ? { bottom: panel.bottom + 'px' } : { top: panel.top + 'px' }), left: panel.left + 'px', minWidth: panel.minWidth + 'px', maxWidth: panel.maxWidth + 'px', maxHeight: panel.maxHeight + 'px' }"
            role="listbox"
            data-floating-panel
        >
            <!-- shrink-0: in this capped-height column a long list squashed the box to a sliver
                 (Kenneth, 7 Oct). The icon and padding match FilterSelect's search. -->
            <div v-if="showSearch" class="relative mb-1 shrink-0">
                <Search class="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                <input
                    v-model="query"
                    type="search"
                    :placeholder="searchPlaceholder"
                    :aria-label="searchPlaceholder.replace(/…$/, '')"
                    class="w-full h-8 pl-8 pr-2 rounded-md border border-gray-200 text-[13px] outline-none focus:border-blue-400"
                    @click.stop
                />
            </div>

            <div class="flex-1 min-h-0 overflow-y-auto">
                <button
                    v-for="option in visibleOptions"
                    :key="option.value"
                    type="button"
                    role="option"
                    :aria-selected="option.value === modelValue"
                    :class="[ITEM, 'items-start', option.value === modelValue ? 'bg-blue-50 text-blue-800 font-semibold' : 'text-slate-700 hover:bg-gray-100']"
                    :title="option.sublabel ? `${option.label} — ${option.sublabel}` : option.label"
                    @click="pick(option)"
                >
                    <BaseAvatar v-if="option.person" :person="option.person" size="sm" class="flex-shrink-0 self-center" />
                    <component
                        :is="option.icon"
                        v-if="option.icon"
                        class="w-4 h-4 flex-shrink-0 mt-0.5"
                        :style="{ color: option.iconColor || '#94a3b8' }"
                    />
                    <!-- Beside an avatar the name is centred with it; the row itself stays top-aligned for two-line options. -->
                    <span class="flex-1 min-w-0" :class="option.person ? 'self-center' : ''">
                        <span class="block truncate" :class="option.sublabel ? 'font-mono text-xs font-semibold text-blue-600' : ''">{{ option.label }}</span>
                        <span v-if="option.sublabel" class="block text-[13px] text-slate-700 truncate">{{ option.sublabel }}</span>
                    </span>
                    <Check v-if="option.value === modelValue" class="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                </button>

                <!-- Two different situations that used to read the same. "No matches" is
                     about the search box; a picker with no options at all is about the
                     project's configuration, and now that System refuses anything outside
                     that list, saying which one it is stops the menu looking broken. -->
                <p v-if="!visibleOptions.length" class="px-2.5 py-3 text-[13px] text-slate-500 text-center">
                    {{ normalised.length ? 'No matches.' : 'Nothing configured yet.' }}
                </p>
            </div>
        </div>
        </Teleport>
    </div>
</template>
